import { Controller, useFormContext } from "react-hook-form";

import { DateTimePickerDropdown } from "src/base-components/DateTimePickerDropdown";
import { Input } from "src/base-components/Input";
import { Select } from "src/base-components/Select";
import { Textarea } from "src/base-components/Textarea";
import { CaseTemplateFormFieldsInner } from "src/clients/case-management";
import { assertUnreachable } from "src/utils/typeUtils";

type FormFieldProps = {
  disabled?: boolean;
  field: CaseTemplateFormFieldsInner;
  hinted?: boolean;
};

export const FormField: React.FC<FormFieldProps> = ({
  field,
  disabled,
  hinted,
}) => {
  const form = useFormContext<Record<string, unknown>>();

  switch (field.type) {
    case "text": {
      if (field.display_as_textarea) {
        return (
          <Textarea
            data-loc={`task-form-field-${field.key}`}
            disabled={disabled}
            errored={!!form.formState.errors[field.key]}
            hinted={hinted}
            placeholder="Add notes"
            {...form.register(field.key)}
          />
        );
      }

      return (
        <Input
          data-loc={`task-form-field-${field.key}`}
          disabled={disabled}
          errored={!!form.formState.errors[field.key]}
          hinted={hinted}
          placeholder="Add notes"
          fullWidth
          {...form.register(field.key)}
        />
      );
    }
    case "integer":
    case "number": {
      return (
        <Input
          data-loc={`task-form-field-${field.key}`}
          disabled={disabled}
          errored={!!form.formState.errors[field.key]}
          hinted={hinted}
          placeholder="Add a value"
          fullWidth
          {...form.register(field.key)}
        />
      );
    }
    case "date": {
      return (
        <Controller
          control={form.control}
          name={field.key}
          render={({ field: dateField }) => {
            const selectedDate =
              typeof dateField.value === "string"
                ? new Date(dateField.value)
                : (dateField.value as Date | undefined);

            return (
              <DateTimePickerDropdown
                disabled={disabled}
                errored={!!form.formState.errors[field.key]}
                hinted={hinted}
                placeholder="Select a date"
                value={selectedDate}
                allowFutureDates
                dateOnly
                onChange={dateField.onChange}
                onReset={() => {
                  dateField.onChange(null);
                  form.trigger(field.key);
                }}
              />
            );
          }}
        />
      );
    }
    case "select": {
      return (
        <Controller
          name={field.key}
          render={({ field: selectField }) => {
            const handleChange = (value: unknown) => {
              selectField.onChange(selectField.value === value ? null : value);
            };

            return (
              <Select
                dataLoc={`task-form-field-${field.key}`}
                disabled={disabled}
                errored={!!form.formState.errors[field.key]}
                hinted={hinted}
                options={field.options.map((option) => ({
                  value: option,
                  key: option,
                }))}
                placeholder="Select option"
                value={selectField.value ?? ""}
                onChange={handleChange}
              />
            );
          }}
        />
      );
    }
    case "multi_select": {
      return (
        <Controller
          name={field.key}
          render={({ field: selectField }) => (
            <Select
              dataLoc={`task-form-field-${field.key}`}
              disabled={disabled}
              errored={!!form.formState.errors[field.key]}
              hinted={hinted}
              options={field.options.map((option) => ({
                value: option,
                key: option,
              }))}
              placeholder="Select option(s)"
              value={selectField.value ?? []}
              multiple
              onChange={selectField.onChange}
            />
          )}
        />
      );
    }

    default: {
      assertUnreachable(field as never);
    }
  }
};
